// API-token (PAT) LIFETIME — one policy per mint reason, and the reason is the
// only input.
//
// WHY THIS IS A TYPE AND NOT A COLUMN DEFAULT. `api_tokens` had no `expires_at`
// at all: every credential the platform has ever minted is permanent until a
// human revokes it by hand. That is survivable for a token a maintainer pastes
// into their own CI, and it is not survivable for one held by a third party —
// an OAuth grant hands a bearer credential to Anthropic's or OpenAI's servers,
// where nobody on our side can see it, use it, or notice it was abandoned.
//
// A nullable column with "some tokens expire" as a convention is how that
// problem survives: two callers, two opinions, no seam. So the LIFETIME IS NOT
// AN ARGUMENT. A mint names WHY it is minting; {@link tokenExpiresAt} answers
// with the lifetime for that reason. A caller cannot pass an expiry, cannot
// pass `null`, and cannot mint without naming a reason — the mint's input type
// has no expiry field, so "a token with a lifetime nobody decided" is not
// expressible rather than merely discouraged.
//
// `null` IS A POLICY ANSWER, NOT AN ABSENT ONE. Some reasons below mint a
// permanent token ON PURPOSE, each for a stated mechanism, and every reason —
// permanent or not — carries its answer on the member itself. (This said "two of
// the three" until a fourth reason arrived; a count falsifies the moment one
// more is added, so it states the property instead.) A reader asking "does a PAT
// expire?" gets an answer from this file rather than from whichever call site
// they happened to open.
//
// Pure types + one clock-taking function — no I/O.

import { z } from 'zod';

/**
 * How long an OAuth-granted connector token lives: **90 days**.
 *
 * The grant mints an ordinary PAT (`apps/api/src/oauth/flow.ts`) and hands it to
 * a hosted client we do not run. Ninety days is the longest window in which a
 * silently-abandoned connector is still plausibly a live one, and it is short
 * enough that a credential nobody is watching dies inside a quarter.
 *
 * **There is no refresh grant, deliberately.** `/oauth/token` accepts
 * `authorization_code` and nothing else, so at expiry the client re-runs
 * `/oauth/authorize` — one browser round trip for a human who is already signed
 * in, since the client keeps its DCR registration. Adding `refresh_token` is a
 * public-contract change (a new `grant_types_supported` member, advertised in
 * discovery and therefore one-way) and belongs in its own decision, not smuggled
 * in behind a TTL. The mint tells the client the number — `/oauth/token`
 * answers `expires_in` per RFC 6749 § 5.1 — so a connector can act on it instead
 * of discovering the expiry as a 401.
 */
export const OAUTH_GRANT_TOKEN_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * How long a platform-OPERATOR credential lives: **1 hour** (#2055).
 *
 * THE FAILURE IT PREVENTS, named rather than assumed (`docs/standards/agent-budgets.md`
 * — a bound survives only by naming one). A credential carrying the five
 * platform-admin writes can approve or suspend any user, move any org's tier and
 * spend cap, and issue capacity on the platform's own margin, ACROSS EVERY
 * TENANT. It is issued to a human at a console, so it lands in a shell history,
 * a terminal scrollback, an `.envrc`, a pasted snippet — and a PAT's scopes are
 * read verbatim off its row at auth time and never re-capped against the
 * holder's live staff grant, so without a TTL a leaked one is a permanent
 * cross-tenant writer that only an explicit revoke of that exact token id ends.
 *
 * That is not hypothetical here. Prod holds exactly one operator credential
 * today — a single `admin:releases` row created 2026-08-21 with no name, no
 * `user_id`, no expiry and `last_used_at` still NULL. Nobody can say who made
 * it, for what, or whether it is still needed, and nothing will ever end it.
 * That row is the shape this TTL exists to stop reproducing.
 *
 * WHY ONE HOUR, AND NOT A DAY. The bound has to cover one operator SITTING, and
 * every staff write on this platform is a single request that completes in
 * seconds: approve a user, set a tier, record an OSS verdict, issue a grant,
 * rename a goal, un-yank a release. An hour covers reading the radar first,
 * deciding, acting, and retrying — with room to spare — while being short enough
 * that a credential which leaks into a log is dead before that log is shipped
 * anywhere. Longer buys no legitimate work; the tasks are not long.
 *
 * IT IS A REAL BOUND, not a decorative one, because the issuance door refuses
 * every PAT (`apps/api/src/routes/operator-tokens.ts`): an operator credential
 * cannot mint its successor, so the hour cannot be chained into a permanent
 * credential laundered through a renewal loop. A TTL an expiring credential can
 * renew for itself is not a TTL.
 */
export const OPERATOR_TOKEN_TTL_MS = 60 * 60 * 1000;

/**
 * The LONGEST an agent's token may live: **24 hours** (`POST /v1/admin/agent-tokens`).
 *
 * THE FAILURE IT PREVENTS. Agents minted through `buildTokenControl` over `kubectl exec`, whose
 * tokens have no lifetime option, so every "1 hour" token was permanent until a person revoked
 * it, and an agent's transcript is a place a credential ends up. A day covers the longest
 * unattended run we hand one a token for (an overnight sweep); past that the agent asks again,
 * and the ask is one more audited row. The caller still states the instant: this is the ceiling,
 * never a default.
 */
export const AGENT_TOKEN_MAX_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Why a token is being minted. Exactly one per door, and the doors are
 * enumerated: `POST /v1/tokens` + the MCP `token mint` action ({@link Pat}),
 * `POST /oauth/token` ({@link OauthGrant}), the enroll-time box connection token
 * ({@link BoxConnection}), and the platform-operator issuance route
 * ({@link OperatorGrant}). A fifth door is a fifth member here, which is the
 * edit that forces someone to decide its lifetime.
 */
export const TokenMintReason = {
  /**
   * A human minting a credential for their own headless use — the dashboard
   * mint, the MCP `token mint` action, `POST /v1/tokens` by hand.
   *
   * **Does not expire.** A PAT is pasted into a `.mcp.json`, a CI secret or a
   * shell profile by the person who owns it; it has no interactive renewal path,
   * so a TTL would break an automation at a moment nobody is watching, with the
   * repair only available to a human who has to notice first. What bounds it
   * instead is that its owner can see it and revoke it — it is listed on Account
   * → Tokens with its prefix, scopes and age, and `resolveApiToken` re-reads
   * `revoked_at` on every single call.
   */
  Pat: 'pat',
  /**
   * An OAuth 2.1 authorization-code grant for a chat connector.
   *
   * **Expires after {@link OAUTH_GRANT_TOKEN_TTL_MS}.** This is the one reason
   * whose holder is a party we do not run and cannot ask.
   */
  OauthGrant: 'oauth_grant',
  /**
   * The enroll-time box connection token (#786/#824) — one capability
   * (`use:integration:slack`), delivered once to a runner as
   * `DEVELOPERZ_API_KEY`.
   *
   * **Does not expire, and this one must not.** Its lifetime is the runner's
   * and is already enforced by a mechanism that cannot drift from it:
   * `revokeRunner` cascades a revoke to the token through `runner_id`. A TTL
   * here would brick a live box mid-run — the box has no re-enrollment path it
   * can drive itself, so the failure would surface as a fleet outage whose cause
   * is a clock.
   */
  BoxConnection: 'box_connection',
  /**
   * A platform-OPERATOR credential (#2055) — the five `admin:*` writes, issued
   * to a signed-in developerz-ai staff human by
   * `POST /v1/admin/operator-tokens` and by no other door.
   *
   * **Expires after {@link OPERATOR_TOKEN_TTL_MS}.** The one reason whose holder
   * can act across every tenant on the platform, and the one whose TTL is a
   * security boundary rather than a hygiene default — see that constant for the
   * failure it prevents and the prod row that shows the shape.
   *
   * It is a fourth member here, and a fourth member is exactly the edit the
   * module header says should force someone to decide a lifetime. Somebody did:
   * one hour, for the reasons enumerated there.
   */
  OperatorGrant: 'operator_grant',
  /**
   * A token an AGENT holds on a person's behalf (`POST /v1/admin/agent-tokens`, or
   * `bun run infra agent-token`): bound to that person, carrying `api_tokens.agent_label`, and
   * the one reason whose caller STATES the instant, inside {@link AGENT_TOKEN_MAX_TTL_MS}
   * ({@link agentTokenExpiresAt}), because how long an agent's job runs is the caller's fact.
   */
  Agent: 'agent',
} as const;
export const TokenMintReasonSchema = z.enum(TokenMintReason);
export type TokenMintReason = z.infer<typeof TokenMintReasonSchema>;

/**
 * The lifetime each reason mints, in ms — `null` = no expiry. Exhaustive over
 * {@link TokenMintReason} by type, so a new member does not compile until it has
 * a lifetime, and the reasons for each answer are on the member above.
 */
export const TOKEN_MINT_TTL_MS = {
  [TokenMintReason.Pat]: null,
  [TokenMintReason.OauthGrant]: OAUTH_GRANT_TOKEN_TTL_MS,
  [TokenMintReason.BoxConnection]: null,
  [TokenMintReason.OperatorGrant]: OPERATOR_TOKEN_TTL_MS,
  [TokenMintReason.Agent]: AGENT_TOKEN_MAX_TTL_MS,
} as const satisfies Record<TokenMintReason, number | null>;

/**
 * The `expires_at` a mint writes: `now + TTL`, or `null` when the reason mints a
 * permanent token. THE mint's only lifetime input — see the module header.
 *
 * Throws on an unknown reason rather than defaulting. The type already refuses
 * one, so this fires only for a value that crossed an untyped boundary (a JSON
 * body, a hand-run script), and the two silent alternatives are both worse: a
 * permanent token nobody asked for, or a dead one at the epoch.
 *
 * OWN PROPERTIES ONLY. A plain object literal inherits `Object.prototype`, so a
 * bare `TTL[reason]` answers a FUNCTION for `toString`/`constructor` and an
 * OBJECT for `__proto__` — each of which sails past an `undefined` check and
 * lands `now + <not a number>`, i.e. an Invalid Date written into
 * `api_tokens.expires_at`. Those are exactly the untyped-boundary values this
 * throw exists for, so `Object.hasOwn` is what makes the sentence above true.
 */
export function tokenExpiresAt(reason: TokenMintReason, now: Date): Date | null {
  if (reason === TokenMintReason.Agent) {
    throw new Error(
      'tokenExpiresAt: an agent token has no lifetime by reason alone — state the instant through agentTokenExpiresAt',
    );
  }
  const ttlMs = Object.hasOwn(TOKEN_MINT_TTL_MS, reason) ? TOKEN_MINT_TTL_MS[reason] : undefined;
  if (ttlMs === undefined) {
    throw new Error(
      `tokenExpiresAt: unknown mint reason "${reason}" — every mint names one of ${Object.values(
        TokenMintReason,
      ).join(', ')}`,
    );
  }
  return ttlMs === null ? null : new Date(now.getTime() + ttlMs);
}

/**
 * The instant an agent token dies: the one asked for, inside the window, or a throw naming which
 * way it missed. The bound itself is admitted; an expiry at or before `now` would mint a dead
 * credential `api_tokens_expiry_ck` also refuses.
 */
export function agentTokenExpiresAt(requested: Date, now: Date): Date {
  if (Number.isNaN(requested.getTime())) {
    throw new Error('agentTokenExpiresAt: expires_at is not a valid instant');
  }
  if (requested.getTime() <= now.getTime()) {
    throw new Error(`agentTokenExpiresAt: ${requested.toISOString()} is not in the future`);
  }
  if (requested.getTime() > now.getTime() + AGENT_TOKEN_MAX_TTL_MS) {
    throw new Error(
      `agentTokenExpiresAt: ${requested.toISOString()} is past the ${AGENT_TOKEN_MAX_TTL_MS / 3_600_000} h maximum an agent token may live`,
    );
  }
  return requested;
}

/**
 * The agent's name on `api_tokens.agent_label`, and so on every audit row its token writes. A
 * slug, because it lands inside "agent <label> for <user>" and a reader greps it.
 */
export const AgentTokenLabelSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9._-]{0,63}$/, 'a lower-case slug, 1-64 chars: a-z 0-9 . _ -');

// ─── NAME: what a human tells two credentials apart by ───────────────────────

/**
 * A token's human label — free text, not a handle. Optional on the wire and
 * nullable in the row: `null` is "nobody ever named this one", which is the
 * honest answer for every token minted before this field existed and for a box
 * connection token no human ever saw.
 *
 * WHY THE ROW HAS TO CARRY IT. Every PAT the platform mints stores the SAME
 * `prefix` — the literal `dev_pat_` (`API_TOKEN_PREFIX`, `@developerz/db`) — and the secret
 * itself exists for exactly one render, so a roster of tokens is a column of
 * identical strings. "Which of these is my CI job?" is unanswerable from
 * anything else the API stores; scopes and dates narrow it and do not name it.
 *
 * NOT slug-constrained the way `webhooks.name` is. That one is a HANDLE —
 * `.maintainer.yml` references it by `webhook_ref`, so its character set is a
 * contract and a clash is a 409. Nothing anywhere references a token by name, so
 * the same rule here would only make the label worse at the one job it has, and
 * two tokens may share a name. Trimmed and bounded: the two things that keep a
 * label a label.
 */
export const ApiTokenNameSchema = z.string().trim().min(1).max(60);

// ─── USE, not lifetime: `api_tokens.last_used_at` ────────────────────────────

/**
 * How stale `api_tokens.last_used_at` may be before the next successful
 * resolution rewrites it: **5 minutes**.
 *
 * WHY A DEBOUNCE AND NOT A WRITE PER REQUEST. The column had two readers and no
 * writer — 39 prod tokens, 0 stamped — so "is this credential still in use?" was
 * unanswerable and a dead connector was indistinguishable from a busy one. The
 * naive repair is one `UPDATE` per authenticated request, and that is a row write
 * on ONE hot row for every step of every coding and review session on every box
 * (they all authenticate with the same PAT), plus the WAL and the dead tuples
 * behind it. The column would go from answering nothing to costing the whole
 * surface.
 *
 * WHY FIVE MINUTES, AND WHY A CONSTANT. The question this column exists to
 * answer — stale connector or live one — is asked in DAYS. Five minutes is three
 * orders of magnitude finer than that and caps the write rate at 12 per token per
 * hour whatever the request rate is. Shorter buys resolution nobody reads; much
 * longer starts to lie about "last used" for a connector polled every few
 * minutes. It is a constant rather than a config knob because an operator holds
 * no information the question does not already answer, and a knob here would be a
 * second opinion about the same window (`docs/standards/env-vars.md`).
 */
export const TOKEN_LAST_USED_DEBOUNCE_MS = 5 * 60 * 1000;

/**
 * The instant a stored `last_used_at` must PRECEDE to be restamped — `now` minus
 * {@link TOKEN_LAST_USED_DEBOUNCE_MS}. A NULL `last_used_at` (never used) is
 * always due, which is the case that turns the column on for every existing row.
 *
 * ONE function because the window is applied TWICE per stamp and the two must not
 * disagree: the resolve decides from the row it already read (so the common case
 * costs no extra round trip), then re-asserts the same predicate in the `UPDATE`'s
 * own `WHERE` (so two concurrent requests write once, not twice). Both
 * comparisons take the instant this returns.
 */
export function lastUsedStaleBefore(now: Date): Date {
  return new Date(now.getTime() - TOKEN_LAST_USED_DEBOUNCE_MS);
}
